import { CheckCircle2 } from 'lucide-react';

import { ReactComponent as BitcoinLogo } from '../../assets/icons/bitcoin_logo.svg';
import { ReactComponent as EthereumLogo } from '../../assets/icons/eth_logo.svg';
import { ReactComponent as ArbitrumLogo } from '../../assets/icons/arbitrum_logo.svg';
import { ReactComponent as TronLogo } from '../../assets/icons/tron_logo.svg';
import { ReactComponent as BaseLogo } from '../../assets/icons/base-logo.svg';
import { Badge } from '../ui/badge';

const iconComponentMap: Record<string, React.FC<{ className?: string }>> = {
  bitcoin: BitcoinLogo,
  ethereum: EthereumLogo,
  arbitrum: ArbitrumLogo,
  tron: TronLogo,
  base: BaseLogo,
};

const inactiveIconStyle: Record<string, string> = {
  bitcoin: 'grayscale opacity-40',
  ethereum: 'grayscale opacity-95',
  arbitrum: 'grayscale opacity-40',
  tron: 'grayscale opacity-40',
  base: 'grayscale opacity-15',
};

type Network = {
  id: keyof typeof iconComponentMap;
  name: string;
  active: boolean;
};

const networks: Network[] = [
  {
    id: 'bitcoin',
    name: 'Bitcoin',
    active: true,
  },
  {
    id: 'ethereum',
    name: 'Ethereum',
    active: true,
  },
  {
    id: 'arbitrum',
    name: 'Arbitrum One',
    active: false,
  },
  {
    id: 'tron',
    name: 'Tron',
    active: false,
  },
  {
    id: 'base',
    name: 'BASE',
    active: false,
  },
];

export const BlockchainNetworks = () => {
  return (
    <div className="flex w-full items-center justify-center">
      <div className="flex flex-col items-center space-y-2">
        <div className="flex items-center space-x-2">
          {networks.slice(0, 3).map((network) => (
            <NetworkItem key={network.id} network={network} />
          ))}
        </div>
        <div className="flex items-center space-x-2">
          {networks.slice(3).map((network) => (
            <NetworkItem key={network.id} network={network} />
          ))}
        </div>
      </div>
    </div>
  );
};

const NetworkItem = ({ network }: { network: Network }) => {
  const Icon = iconComponentMap[network.id];
  const isActive = network.active;

  return (
    <div
      className={`relative flex items-center gap-1.5 rounded-full border px-3 py-1.5 transition-all duration-200 hover:scale-105 ${
        isActive
          ? 'cursor-pointer border-border bg-background shadow-sm hover:shadow'
          : 'cursor-default border-border/50 bg-muted/50'
      } `}
    >
      <div
        className={`flex h-5 w-5 items-center justify-center rounded-full ${
          isActive
            ? 'bg-background text-foreground'
            : 'bg-muted text-muted-foreground'
        } `}
      >
        <Icon
          className={`h-5 w-5 text-inherit ${
            !isActive ? inactiveIconStyle[network.id] : ''
          }`}
        />
      </div>

      <div className="flex items-center gap-1 whitespace-nowrap">
        <span
          className={`${
            isActive ? 'text-foreground' : 'text-muted-foreground'
          }`}
        >
          {network.name}
        </span>
        {isActive ? (
          <CheckCircle2 className="mt-0.5 h-3 w-3 text-primary" />
        ) : (
          <Badge
            variant="outline"
            className="ml-1 border-border bg-muted text-xs font-normal text-secondary-foreground"
          >
            Coming Soon
          </Badge>
        )}
      </div>
    </div>
  );
};
